import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Link,
} from '@mui/material';
import {
  Assignment,
  Work,
  LocalShipping,
  AttachMoney,
  Campaign,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { headlineService } from '../../services/headlineService';
import usePageTitle from '../../utils/usePageTitle';

const Dashboard = () => {
  usePageTitle('Dashboard');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [headlines, setHeadlines] = useState([]);

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'Carson_AdminUser':
        return 'Admin user';
      case 'Carson_TechUser':
        return 'Technician';
      default:
        return role;
    }
  };

  const quickActions = [
    {
      title: 'Create CRF',
      description: 'Create a new Customer Request Form',
      icon: <Assignment />,
      path: '/projects/new',
      roles: ['Admin', 'Technician', 'User'],
    },
    {
      title: 'New Work Order',
      description: 'Create a new work order',
      icon: <Work />,
      path: '/workorders/new',
      roles: ['Admin', 'Technician'],
    },
    {
      title: 'Delivery Request',
      description: 'Create a delivery request',
      icon: <LocalShipping />,
      path: '/delivery/new',
      roles: ['Admin', 'Technician'],
    },
    {
      title: 'Financial Reports',
      description: 'View financial reports',
      icon: <AttachMoney />,
      path: '/reports/financial',
      roles: ['Admin'],
    },
  ];

  const filteredActions = quickActions.filter(action =>
    action.roles.includes(user?.role)
  );

  useEffect(() => {
    const fetchHeadlines = async () => {
      const currentHeadlines = await headlineService.getCurrentHeadlines();
      setHeadlines(currentHeadlines);
    };
    fetchHeadlines();
  }, []);

  return (
    <Box component="section" aria-labelledby="dashboard-title">
      <Typography variant="h4" gutterBottom component="h1" id="dashboard-title">
        Welcome back, {user?.username || user?.name || 'User'}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom component="p">
        {getRoleDisplayName(user?.role)} Dashboard
      </Typography>

      <Box component="section" sx={{ mt: 2 }} aria-labelledby="quick-actions-title">
        <Typography variant="h5" component="h2" id="quick-actions-title" sx={{ mb: 2 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={3} role="list">
        {filteredActions.map((action, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index} role="listitem">
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box aria-hidden="true">{action.icon}</Box>
                  <Typography variant="h6" sx={{ ml: 1 }} component="h3">
                    {action.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {action.description}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(action.path)}
                  sx={{ mt: 1 }}
                  aria-label={`Navigate to ${action.title}`}
                >
                  Go
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
        </Grid>
      </Box>

      {headlines.length > 0 && (
        <Box sx={{ mt: 3, width: '100%' }} component="section" aria-labelledby="headlines-title">
          <Card sx={{ width: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Campaign sx={{ mr: 1 }} aria-hidden="true" />
                  <Typography variant="h6" component="h2" id="headlines-title">
                    Headlines
                  </Typography>
                </Box>
                <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {headlines.map((headline, index) => (
                  <Box component="li" key={headline.headline_id} sx={{ mb: index < headlines.length - 1 ? 2 : 0 }}>
                  <Alert 
                    severity="info" 
                    role="article"
                    aria-labelledby={`headline-${headline.headline_id}-title`}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }} component="h3" id={`headline-${headline.headline_id}-title`}>
                      {headline.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {headline.headline}
                    </Typography>
                    {headline.link && (
                      <Link 
                        href={headline.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        aria-label={`Open ${headline.title} link in new window`}
                        sx={{ 
                          color: '#0d47a1',
                          textDecorationThickness: '2px',
                          '&:hover': { color: '#0a3d91' },
                          '&:focus-visible': {
                            outline: '3px solid #000000',
                            outlineOffset: '2px',
                            borderRadius: '2px',
                          }
                        }}
                      >
                        {headline.link}
                      </Link>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      Active: {headline.start_date} - {headline.end_date}
                    </Typography>
                  </Alert>
                  </Box>
                ))}
                </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      <Box sx={{ mt: 3, width: '100%' }} component="section" aria-labelledby="recent-activity-title">
        <Card sx={{ width: '100%' }}>
          <CardContent>
              <Typography variant="h6" gutterBottom component="h2" id="recent-activity-title">
                Recent Activity
              </Typography>
              <Typography variant="body2" color="text.secondary" role="status" aria-live="polite">
                No recent activity to display.
              </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;